import { indexMdByNotionId, normalizeExportPath } from '@couplespace/core/notion-export';

import {
  readTextFile,
  resolveFilePath,
  scanFolderDirectoryHandle,
  scanFolderFileList,
  type FolderFileScan,
} from '../folder-scan/scan-folder.web.js';

export type ExportFolderScan = FolderFileScan & {
  indexHtml: string;
  mdByNotionId: Map<string, string>;
};

function assertLooksLikeExport(base: FolderFileScan): void {
  // Newer Notion exports drop index.html; the tree is reconstructed from the
  // folder layout instead. Require at least one markdown/CSV file so we still
  // reject folders that clearly aren't a Notion export.
  const hasIndex = base.files.has('index.html');
  const hasContent = base.paths.some((p) => /\.(md|csv)$/i.test(p));
  if (!hasIndex && !hasContent) {
    throw new Error('This folder does not look like a Notion export (no index.html, .md, or .csv files).');
  }
}

async function toExportFolderScan(base: FolderFileScan): Promise<ExportFolderScan> {
  assertLooksLikeExport(base);
  const indexFile = base.files.get('index.html');
  return {
    ...base,
    indexHtml: indexFile ? await indexFile.text() : '',
    mdByNotionId: indexMdByNotionId(base.paths),
  };
}

export async function scanDirectoryHandle(
  dir: FileSystemDirectoryHandle,
): Promise<ExportFolderScan> {
  return toExportFolderScan(await scanFolderDirectoryHandle(dir));
}

export async function scanFileList(fileList: FileList): Promise<ExportFolderScan> {
  return toExportFolderScan(await scanFolderFileList(fileList));
}

export { readTextFile, resolveFilePath };

export async function readPageMarkdown(
  scan: ExportFolderScan,
  mdPath: string,
  notionId: string | null,
): Promise<string | null> {
  const resolved = resolveMdPath(scan, mdPath, notionId);
  if (!resolved) {
    return null;
  }
  return readTextFile(scan.files, resolved);
}

export function resolveMdPath(
  scan: ExportFolderScan,
  mdPath: string,
  notionId: string | null,
): string | null {
  const resolved = resolveFilePath(scan.files, mdPath);
  if (resolved) {
    return resolved;
  }
  if (notionId && scan.mdByNotionId.has(notionId)) {
    return scan.mdByNotionId.get(notionId)!;
  }
  return null;
}

export function normalizeExportPathForScan(path: string): string {
  return normalizeExportPath(path);
}
